/**
 * FaqContainer - FAQ accordion list container.
 * Custom element: <faq-container>
 *
 * Attributes (from $info):
 *   no-results-text  String  -
 *
 * Refs:
 *   [data-ref="data"]  (required)
 *   [data-ref="searchContainer"]  (required)
 *   [data-ref="searchInput"]  (required)
 *   [data-ref="searchResultsContainer"]  (required)
 *   [data-ref="categoryButton"]  (required)
 *   [data-ref="categoryContainer"]  (required)
 *   [data-ref="categoryDropdown"]  (required)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) => defineProperty(target, "name", {
  value: functionName,
  configurable: !0
});
import "./component-FakeDropdown.js";
import { ThemeComponent, scrollToElement$1 as scrollToElement } from "./theme.js";
import { debounce } from "./vendor.js";
class FaqContainer extends ThemeComponent {
  constructor() {
    super(...arguments), this.faqData = null;
  }
  init() {
    if (this.listenForCategoryButtonClick(), this.listenForCategoryDropdownChange(), this.initObserver(), this.faqData = this.parseData(), this.faqData === null) {
      this.$log.error("FAQ - unable to access FAQ data");
      return;
    }
    this.handleSearch(), this.handleCloseEvents();
  }
  listenForCategoryButtonClick() {
    for (const categoryButtonElement of this.categoryButton) this.$listen(categoryButtonElement, "click", () => {
      const categoryTitle = categoryButtonElement.getAttribute("data-category-title");
      for (const categoryContainerElement of this.categoryContainer) categoryContainerElement.getAttribute("data-category-title") === categoryTitle && scrollToElement(categoryContainerElement, !1);
    });
  }
  listenForCategoryDropdownChange() {
    this.$subscribe(this.categoryDropdown.onChange, selectedCategory => {
      for (const categoryContainerElement of this.categoryContainer) categoryContainerElement.getAttribute("data-category-title") === selectedCategory && scrollToElement(categoryContainerElement, !1);
    });
  }
  initObserver() {
    new MutationObserver(() => {
      const resultItems = this.searchResultsContainer.querySelectorAll(".faq-search__results__item:not(.faq-search__results__item--no-results)");
      for (const resultItem of resultItems) {
        const clickableResult = resultItem;
        this.$listen(clickableResult, "click", () => {
          this.handleResultClick(clickableResult);
        });
      }
    }).observe(this.searchResultsContainer, {
      subtree: !0,
      childList: !0
    });
  }
  handleResultClick(clickedResult) {
    this.searchContainer.classList.remove("faq-search--results-active");
    let identifier = clickedResult.innerHTML.toLowerCase();
    identifier = identifier.replace("&amp;", ""), identifier = identifier.replace("&", ""), identifier = identifier.replace(/[^\w\s]/gi, ""), identifier = identifier.replace(/[^a-z0-9]+/g, "-"), identifier = identifier.replace(/-$/, ""), identifier = identifier.replace(/^-/, "");
    const faqIdentifier = identifier,
      faqEntry = this.querySelector(`[data-faq-identifier=${faqIdentifier}]`);
    if (faqEntry === null) return;
    const accordion = faqEntry.querySelector(".accordion-container");
    if (accordion && !accordion.classList.contains("accordion-container--open")) {
      const accordionHeader = faqEntry.querySelector(".accordion-container__header");
      accordionHeader && accordionHeader.click();
    }
    scrollToElement(faqEntry, !1);
  }
  parseData() {
    let parsedData = null;
    if (this.data) try {
      parsedData = JSON.parse(this.data.innerHTML);
    } catch (parseError) {
      this.$log.error("FAQ parse error", {
        event: parseError
      });
    }
    return parsedData;
  }
  handleSearch() {
    this.$listen(this.searchInput, "input", debounce(inputEvent => {
      this.searchContainer.classList.add("faq-search--results-active");
      const searchTerm = this.searchInput.value,
        matchingQuestions = this.filterResults(searchTerm);
      this.updateResultsMarkup(matchingQuestions);
    }));
  }
  filterResults(searchTerm) {
    let matches = [];
    if (this.faqData !== null) for (const faqItem of this.faqData) {
      const needle = searchTerm.toLowerCase(),
        question = faqItem.question.toLowerCase(),
        answer = faqItem.answer.toLowerCase();
      (question.includes(needle) || answer.includes(needle)) && matches.push(faqItem.question);
    }
    return matches = matches.slice(0, 5), matches;
  }
  updateResultsMarkup(questions) {
    let resultsMarkup = "";
    for (const questionText of questions) {
      const itemMarkup = `
                <div class="faq-search__results__item text-left">
                    ${questionText}
                </div>
            `;
      resultsMarkup += itemMarkup;
    }
    resultsMarkup === "" && (resultsMarkup = `
                <div class="faq-search__results__item faq-search__results__item--no-results">
                    ${this.noResultsText}
                </div>
            `), this.searchResultsContainer.innerHTML = resultsMarkup;
  }
  handleCloseEvents() {
    this.$listen(document.body, "click", bodyClickEvent => {
      const clickedElement = bodyClickEvent.target;
      this.searchContainer === clickedElement || this.searchContainer.contains(clickedElement) || this.searchContainer.classList.remove("faq-search--results-active");
    });
  }
}
setFunctionName(FaqContainer, "FaqContainer");
const propertyTypes = {
  String
};
FaqContainer.prototype.$name = "FaqContainer";
FaqContainer.prototype.$info = JSON.parse('{"name":"FaqContainer","meta":{"isLegacy":false,"selector":"faq-container"},"props":[{"attr":"no-results-text","reflectAttr":false,"propertyKey":"noResultsText","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string"}],"refs":[{"selector":"[data-ref=\\"data\\"]","required":true,"propertyKey":"data","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLScriptElement"},{"selector":"[data-ref=\\"searchContainer\\"]","required":true,"propertyKey":"searchContainer","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"searchInput\\"]","required":true,"propertyKey":"searchInput","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLInputElement"},{"selector":"[data-ref=\\"searchResultsContainer\\"]","required":true,"propertyKey":"searchResultsContainer","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"categoryButton\\"]","required":true,"propertyKey":"categoryButton","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"HTMLButtonElement[]"},{"selector":"[data-ref=\\"categoryContainer\\"]","required":true,"propertyKey":"categoryContainer","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"HTMLElement[]"},{"selector":"[data-ref=\\"categoryDropdown\\"]","required":true,"propertyKey":"categoryDropdown","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"FakeDropdown"}]}');
FaqContainer.prototype.$info.component = FaqContainer;
for (const prop of FaqContainer.prototype.$info.props) prop.propertyType = propertyTypes[prop.propertyType];
window.componentService.registerComponent("Faq/FaqContainer.ts", {
  FaqContainer: FaqContainer
}, void 0);